import React, { useState, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { HubSidebar } from "@/components/hub/HubSidebar";
import { useAuth } from "@/context/AuthContext";
import { useList, useUsersMap, useInvalidate } from "@/lib/hooks";
import api, { apiError } from "@/lib/api";
import { PageHeader, Panel, EmptyState, Field } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { uploadAsset, mediaUrl } from "@/lib/upload";
import { Plus, ChatsCircle, PaperPlaneRight, UsersThree, Truck, Buildings, Trash, Paperclip, ImageSquare, Microphone, Stop, FileArrowDown, X, Play } from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPE_ICON = { team: UsersThree, customer: Buildings, driver: Truck, carrier: Buildings, dispatch: UsersThree, sales: UsersThree };
const CATEGORIES = [
  { key: "driver", label: "Driver Groups", icon: Truck },
  { key: "carrier", label: "Carrier Groups", icon: Buildings },
  { key: "dispatch", label: "Dispatch Team", icon: UsersThree },
  { key: "sales", label: "Sales Team", icon: UsersThree },
];
const empty = { name: "", type: "driver", description: "", members: [] };

export default function Groups() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const invalidate = useInvalidate();
  const { data: groups = [], isLoading } = useList("groups", "/groups");
  const [active, setActive] = useState(null);
  useEffect(() => {
    if (active || !groups.length) return;
    setActive(groups.find((g) => g.type === (user.role === "sales" ? "sales" : "dispatch")) || null);
  }, [groups, active, user.role]);
  const removeGroup = async (id) => { if (!window.confirm("Delete this conversation and its messages?")) return; await api.delete(`/groups/${id}`); setActive(null); invalidate("groups"); };
  return (
    <div>
      <PageHeader title="Communication Hub" subtitle="Team channels plus one-to-one conversations with every carrier and driver" />
      {isLoading ? <div className="text-slate-500">Loading…</div> : (
        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4 h-[calc(100vh-220px)] min-h-[500px]">
          <HubSidebar groups={groups} active={active} setActive={setActive} user={user} />
          {active ? <ChatPanel key={active.id} group={active} user={user} isAdmin={isAdmin} onDelete={() => removeGroup(active.id)} /> : <Panel><EmptyState icon={ChatsCircle} title="Pick a conversation" hint="Choose a team channel, a carrier or a driver on the left." /></Panel>}
        </div>
      )}
    </div>
  );
}

function ChatPanel({ group, user, isAdmin, onDelete }) {
  const qc = useQueryClient();
  const queryKey = ["messages", group.id];
  const { data: messages = [] } = useList(queryKey, `/groups/${group.id}/messages`, { refetchInterval: 10000 });
  const [text, setText] = useState("");
  const [live, setLive] = useState(false);
  const [pending, setPending] = useState(null); // {url, attachment_type, attachment_name}
  const [uploading, setUploading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const wsRef = useRef(null);
  const endRef = useRef(null);
  const photoRef = useRef(null);
  const fileRef = useRef(null);
  const recRef = useRef(null);
  const recTimer = useRef(null);
  const Icon = TYPE_ICON[group.type] || ChatsCircle;

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  useEffect(() => {
    let closed = false;
    let retry;
    const connect = () => {
      const wsBase = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");
      let ws;
      try { ws = new WebSocket(`${wsBase}/api/ws/groups/${group.id}`); }
      catch (e) { return; }
      wsRef.current = ws;
      ws.onopen = () => setLive(true);
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          qc.setQueryData(queryKey, (old = []) => (old.some((m) => m.id === msg.id) ? old : [...old, msg]));
        } catch (e) { console.error("Failed to parse chat message", e); }
      };
      ws.onclose = () => {
        setLive(false);
        if (!closed) retry = setTimeout(connect, 2500);
      };
      ws.onerror = () => { try { ws.close(); } catch (e) { console.debug("ws close after error", e); } };
    };
    connect();
    return () => { closed = true; clearTimeout(retry); try { wsRef.current?.close(); } catch (e) {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.id]);

  const deliver = async (payload) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(payload));
    } else {
      try { await api.post("/messages", { group_id: group.id, ...payload }); qc.invalidateQueries({ queryKey }); }
      catch (err) { toast.error("Failed to send"); }
    }
  };

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim() && !pending) return;
    const payload = { text: text.trim() };
    if (pending) { payload.attachment_url = pending.url; payload.attachment_type = pending.attachment_type; payload.attachment_name = pending.attachment_name; if (pending.duration) payload.duration = pending.duration; }
    setText(""); setPending(null);
    await deliver(payload);
  };

  const onFile = async (e, kind) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadAsset(file, "chat");
      setPending({ url: res.url, attachment_type: kind, attachment_name: file.name });
    } catch (err) { toast.error("Upload failed"); }
    finally { setUploading(false); if (e.target) e.target.value = ""; }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (ev) => ev.data.size && chunks.push(ev.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(recTimer.current);
        const secs = recSecs;
        setRecording(false); setRecSecs(0);
        const blob = new Blob(chunks, { type: "audio/webm" });
        const file = new File([blob], `voice-${Date.now()}.webm`, { type: "audio/webm" });
        setUploading(true);
        try {
          const res = await uploadAsset(file, "chat");
          await deliver({ text: "", attachment_url: res.url, attachment_type: "voice", attachment_name: "Voice note", duration: secs });
        } catch (err) { toast.error("Voice upload failed"); }
        finally { setUploading(false); }
      };
      recRef.current = mr;
      mr.start();
      setRecording(true); setRecSecs(0);
      recTimer.current = setInterval(() => setRecSecs((s) => s + 1), 1000);
    } catch (err) { toast.error("Microphone access denied"); }
  };
  const stopRec = () => { try { recRef.current?.stop(); } catch (e) {} };

  return (
    <Panel className="flex flex-col overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2.5"><Icon size={20} weight="duotone" className="text-blue-600" /><div><p className="font-heading font-bold text-slate-900">{group.name}</p><p className="text-xs text-slate-500 flex items-center gap-1.5">{group.description || ({ carrier_dm: "Carrier chat", driver_dm: "Driver chat", sales: "Sales team channel", dispatch: "Dispatch team channel" }[group.type] || `${group.type} group`)}<span className={cn("inline-flex items-center gap-1 ml-1", live ? "text-green-400" : "text-slate-500")}><span className={cn("w-1.5 h-1.5 rounded-full", live ? "bg-green-400 animate-pulse" : "bg-slate-500")} />{live ? "Live" : "Reconnecting"}</span></p></div></div>
        {isAdmin && <button onClick={onDelete} className="p-1.5 text-slate-500 hover:text-red-400"><Trash size={16} /></button>}
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 ? <EmptyState icon={ChatsCircle} title="No messages yet" hint="Start the conversation." /> : messages.map((m) => {
          const mine = m.sender_id === user.id;
          return (
            <div key={m.id} className={cn("flex", mine ? "justify-end" : "justify-start")} data-testid={`message-${m.id}`}>
              <div className={cn("max-w-[75%] rounded-md px-3 py-2", mine ? "bg-blue-600 text-slate-950" : "bg-slate-100 text-slate-900")}>
                {!mine && <p className="text-[11px] font-semibold text-blue-600 mb-0.5">{m.sender_name} <span className="text-slate-500 font-normal capitalize">· {m.sender_title || m.sender_role}</span>{m.sender_direct_line && <span className="text-slate-400 font-normal"> · {m.sender_direct_line}</span>}</p>}
                {m.attachment_url && m.attachment_type === "image" && (
                  <a href={mediaUrl(m.attachment_url)} target="_blank" rel="noreferrer"><img src={mediaUrl(m.attachment_url)} alt={m.attachment_name || "image"} className="rounded-md max-w-[240px] max-h-60 object-cover mb-1" data-testid={`msg-image-${m.id}`} /></a>
                )}
                {m.attachment_url && m.attachment_type === "voice" && (
                  <audio controls src={mediaUrl(m.attachment_url)} className="h-9 mb-1 max-w-[240px]" data-testid={`msg-voice-${m.id}`} />
                )}
                {m.attachment_url && m.attachment_type === "file" && (
                  <a href={mediaUrl(m.attachment_url)} target="_blank" rel="noreferrer" className={cn("flex items-center gap-2 rounded-md px-2.5 py-2 mb-1 text-sm font-medium", mine ? "bg-blue-500/60" : "bg-white border border-slate-200")} data-testid={`msg-file-${m.id}`}>
                    <FileArrowDown size={18} /> <span className="truncate max-w-[180px]">{m.attachment_name || "Download file"}</span>
                  </a>
                )}
                {m.text && <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>}
                <p className={cn("text-[10px] mt-0.5", mine ? "text-slate-700" : "text-slate-500")}>{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <div className="border-t border-slate-200 shrink-0">
        {pending && (
          <div className="px-3 pt-2 flex items-center gap-2">
            <div className="flex items-center gap-2 bg-slate-100 rounded-md px-2.5 py-1.5 text-sm text-slate-700">
              {pending.attachment_type === "image" ? <ImageSquare size={16} /> : pending.attachment_type === "voice" ? <Microphone size={16} /> : <Paperclip size={16} />}
              <span className="truncate max-w-[200px]">{pending.attachment_name}</span>
              <button onClick={() => setPending(null)} className="text-slate-400 hover:text-red-500" data-testid="chat-clear-attachment"><X size={14} /></button>
            </div>
          </div>
        )}
        {recording && (
          <div className="px-3 pt-2 flex items-center gap-2 text-sm text-red-600 font-medium">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> Recording… {recSecs}s
          </div>
        )}
        <form onSubmit={send} className="p-3 flex items-center gap-2">
          <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e, "image")} data-testid="chat-photo-input" />
          <input ref={fileRef} type="file" className="hidden" onChange={(e) => onFile(e, "file")} data-testid="chat-file-input" />
          <button type="button" onClick={() => photoRef.current?.click()} disabled={uploading || recording} className="text-slate-500 hover:text-blue-600 transition-colors shrink-0" title="Send photo" data-testid="chat-photo-btn"><ImageSquare size={21} weight="duotone" /></button>
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading || recording} className="text-slate-500 hover:text-blue-600 transition-colors shrink-0" title="Attach file" data-testid="chat-file-btn"><Paperclip size={20} weight="duotone" /></button>
          <button type="button" onClick={recording ? stopRec : startRec} disabled={uploading} className={cn("transition-colors shrink-0", recording ? "text-red-500" : "text-slate-500 hover:text-blue-600")} title={recording ? "Stop" : "Record voice"} data-testid="chat-voice-btn">{recording ? <Stop size={21} weight="fill" /> : <Microphone size={21} weight="duotone" />}</button>
          <Input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder={uploading ? "Uploading…" : "Type a message…"} className="bg-white border-slate-300" disabled={recording} />
          <Button type="submit" data-testid="send-message-btn" disabled={uploading || recording} className="bg-blue-600 text-white hover:bg-blue-700 shrink-0"><PaperPlaneRight size={18} weight="fill" /></Button>
        </form>
      </div>
    </Panel>
  );
}
